/**
 * 🧭 10-03 /explore "Coming up at the house" (HOUSE_OCTOBER_UIUX_BATCH_SPEC_2026_10_01 §3.6:
 * R15 "Sheet + Live now", R24 #3 resident plans in our words). Pure helpers over the room
 * list's `house_upcoming` field (the house row only; backend houseCalendar.exploreUpcoming):
 *   { today, tz, rows: [{ day, emoji, label, at?, live?, room?, by? }] }
 * It rides the listing only while the backend's EXPLORE_UPCOMING_ENABLED is on, so with it
 * absent every helper here returns nothing and the page renders exactly as before.
 *
 * ⚠️ Day words come from the HOUSE clock (the payload's tz), never the visitor's own date:
 * a visitor in Tokyo would read "tomorrow" for a night the house calls "tonight". Times read
 * on the house clock too (Nina 09-24: "the time should reflect the time in the Claude house");
 * the sheet says "house time" once, in its header.
 * ⚠️ "On now" only when the engine STARTED the night (`live`, from the listing), never from a
 * clock: a passed start reads "Starting soon" for 20 minutes (the rooms' STRIP_GRACE_MS), then
 * "Tonight". The listing is load-time only (the lobby socket never refreshes its rows).
 */
import { HOUSE_SPACE_PATH } from '../data/whatsNew';
import { dayDiff, weekdayOf, shortDate, clockWords, STRIP_GRACE_MS, STRIP_HOT_MS, HOUSE_TZ } from './calendarView';
import { calendarGlyph } from '../components/ChatGame/CalendarDoor';
import { ROOM_NAMES } from './roomTransit';

export const UPCOMING_MAX = 3;   // the backend's cap too: fits the phone sheet without scrolling
export const UPCOMING_DAYS = 14; // R7: two weeks
export const UPCOMING_SEEN_KEY = 'whats_new_events_seen'; // the dot: nights this device has seen on their day

const KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const ROOM_RE = /^[a-z][a-z0-9_-]{0,31}$/; // a room id (housePlaces ids: orchard, den, roof...)
const CAL = '\u{1F5D3}\u{FE0F}';
// The outings' names (backend housePlaces.OUTINGS); the house rooms come from roomTransit.ROOM_NAMES.
const OUTING_NAMES = { orchard: 'the orchard', lighthouse: 'the lighthouse', maze: 'the corn maze', patch: 'the pumpkin patch' };

/** A space's canonical page (the room list's own link). */
export const spacePathFor = (id) => `/game-step?gameId=${encodeURIComponent(id)}`;

/** The house day ('YYYY-MM-DD') of an instant on the house clock, or null. */
export function houseDayOf(ms, tz = HOUSE_TZ) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(ms));
    const part = (t) => (parts.find((p) => p.type === t) || {}).value;
    const key = `${part('year')}-${part('month')}-${part('day')}`;
    return KEY_RE.test(key) ? key : null;
  } catch (e) { return null; }
}

/** The hour (0-23) of an instant on the house clock, or null. */
export function houseHourOf(ms, tz = HOUSE_TZ) {
  try {
    const h = Number(new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hourCycle: 'h23' }).format(new Date(ms)));
    return Number.isFinite(h) ? h % 24 : null;
  } catch (e) { return null; }
}

/** '4pm' · '7:30pm': an instant on the house clock. */
export function houseTimeOf(ms, tz = HOUSE_TZ) {
  try {
    return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz })
      .replace(':00', '').replace(/\s?([AP])M/i, (_, x) => `${x.toLowerCase()}m`).replace(/\s+/g, '');
  } catch (e) { return ''; }
}

const roomWords = (room) => {
  const name = OUTING_NAMES[room] || (ROOM_NAMES[room] ? ROOM_NAMES[room][1] : null);
  return name ? `to ${name}` : null;
};
const evening = (hour) => hour !== null && hour >= 16; // the engine's own herald rule ("TONIGHT" from 4 PM)

/** The one time line: 'On now' · 'Starting soon' · 'in 2h 40m' · 'Tonight 7pm' · 'Tomorrow 4pm' · 'Sat 4pm' · 'Thu, Oct 8 · 8pm'. */
export function whenWords(row, now, today, tz = HOUSE_TZ) {
  if (row.live === true) return 'On now';
  const diff = dayDiff(today, row.day);
  const at = Date.parse(row.at);
  const has = Number.isFinite(at);
  const time = has ? houseTimeOf(at, tz) : '';
  const late = evening(houseHourOf(has ? at : now, tz)) ? 'Tonight' : 'Today';
  if (diff === 0) {
    if (!has) return late;
    const ms = at - now;
    if (ms > STRIP_HOT_MS) return time ? `${late} ${time}` : late;
    if (ms > 0) return clockWords(ms);
    if (ms > -STRIP_GRACE_MS) return 'Starting soon'; // passed, and the engine has not started it
    return late;
  }
  if (diff === 1) return time ? `Tomorrow ${time}` : 'Tomorrow';
  const wd = weekdayOf(row.day).slice(0, 3);
  if (diff <= 6) return time ? `${wd} ${time}` : wd;
  return time ? `${wd}, ${shortDate(row.day)} · ${time}` : `${wd}, ${shortDate(row.day)}`;
}

/**
 * The sheet's rows, computed when it opens: [{ key, glyph, label, when, live, today, by, to, href }].
 * [] for a missing or empty field. `today` = on the night's house day (or live): the dot's test.
 * `href` = the house; while a night runs with a known room, the room's own deep link that
 * waits at the door (?room=&view=peek). `spaceId` = the house row's id (else HOUSE_SPACE_PATH).
 */
export function upcomingRows(field, { now = Date.now(), spaceId = null } = {}) {
  if (!field || typeof field !== 'object' || !Array.isArray(field.rows) || !field.rows.length) return [];
  const tz = typeof field.tz === 'string' && field.tz.includes('/') ? field.tz : HOUSE_TZ;
  const today = houseDayOf(now, tz) || (KEY_RE.test(String(field.today)) ? field.today : null);
  if (!today) return [];
  const base = spaceId ? spacePathFor(spaceId) : HOUSE_SPACE_PATH;
  const out = [];
  for (const r of field.rows) {
    if (out.length >= UPCOMING_MAX) break;
    if (!r || typeof r !== 'object' || !KEY_RE.test(String(r.day)) || typeof r.label !== 'string' || !r.label.trim()) continue;
    const live = r.live === true;
    const diff = dayDiff(today, r.day);
    if (!live && (diff === null || diff < 0 || diff > UPCOMING_DAYS)) continue; // a night that slipped past while the page sat open
    const room = live && typeof r.room === 'string' && ROOM_RE.test(r.room) ? r.room : null;
    const by = typeof r.by === 'string' && r.by.trim() ? r.by.trim().slice(0, 24) : null;
    out.push({
      key: `${r.day}|${r.label}`,
      glyph: calendarGlyph(typeof r.emoji === 'string' && r.emoji ? r.emoji : CAL),
      label: r.label.trim().slice(0, 60),
      when: whenWords(r, now, today, tz),
      live,
      today: live || diff === 0,
      by: by ? `Planned by ${by}` : null,
      to: room ? roomWords(room) : null,
      href: room ? `${base}&room=${encodeURIComponent(room)}&view=peek` : base,
    });
  }
  return out;
}

/** The night the engine is running in this room right now (the listing's word), or null. */
export function liveNight(space) {
  const f = space && space.house_upcoming;
  if (!f || !Array.isArray(f.rows)) return null;
  return f.rows.find((r) => r && r.live === true && typeof r.label === 'string' && r.label.trim()) || null;
}

/** Live now's line for a running night: '🍎 Apple picking is on'. */
export const liveNightLine = (r) => `${calendarGlyph(typeof r.emoji === 'string' && r.emoji ? r.emoji : CAL)} ${r.label.trim().slice(0, 60)} is on`;

/** The dot (pill only): a night on its house day, or running, that this device has not
 *  opened the sheet on yet. Opening the sheet marks them seen (markEventsSeen). */
export function readSeenEvents(storage) {
  try {
    const v = JSON.parse((storage && storage.getItem(UPCOMING_SEEN_KEY)) || '[]');
    return Array.isArray(v) ? v.filter((k) => typeof k === 'string') : [];
  } catch (e) { return []; }
}
export const eventDot = (rows, seen) => (rows || []).some((r) => r.today && !(seen || []).includes(r.key));
export function markEventsSeen(storage, rows) {
  const keys = (rows || []).filter((r) => r.today).map((r) => r.key);
  if (!keys.length) return readSeenEvents(storage);
  const next = [...new Set([...readSeenEvents(storage), ...keys])].slice(-20); // a short memory: a night's key never comes back
  try { if (storage) storage.setItem(UPCOMING_SEEN_KEY, JSON.stringify(next)); } catch (e) { /* private mode: the dot comes back next visit */ }
  return next;
}
